import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import translate from "../../../i18n/translate";
import { isEmpty } from "lodash";
import { getInsurances } from "../../../actions/insuranceAction";
import { useIntl } from "react-intl";
import { Box, Typography, Container, Card, CardContent, Button as MuiButton } from "@mui/material";
import Stepper from "../../common/Stepper";
import VehicleStepper from "./VehicleStepper";
import LoadStepper from "./LoadStepper";
import PriceStepper from "./PriceStepper";
import { getInsuranceCalculations } from "../../../actions/insuranceAction";
import { refreshColumnStyle } from "../../Gaming/reusableFunctions";
import InsuranceCard from "./InsuranceCard";
import SetHomePageButton from "../../common/SetHomePageButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import "./style.css";

const Languages = {
  en: "english",
  ar: "arabic",
  il: "israel",
};

const Insurance = ({
  getInsurances,
  insurances,
  getInsuranceCalculations,
  insuranceCalculations,
  submitInsuranceLoading,
  isDarktheme,
}) => {
  const intl = useIntl();
  
  const driverTypes = useMemo(() => [
    { value: "private", label: intl.formatMessage({ id: "Private" }) },
    { value: "commercial", label: intl.formatMessage({ id: "commercial" }) },
    {
      value: "taxi_1_driver",
      label: intl.formatMessage({ id: "One Driver - Taxi" }),
    },
    {
      value: "taxi_2_drivers",
      label: intl.formatMessage({ id: "Two Drivers - Taxi" }),
    },
    {
      value: "taxi_multiple_drivers",
      label: intl.formatMessage({ id: "Multiple Driver - Taxi" }),
    },
  ], [intl]);

  const [columnStyle, setColumnStyle] = useState(
    "col-lg-3 col-md-4 col-sm-6 card-md"
  );
  const [steps, setSteps] = useState([
    "Vehicle Type",
    "Vehicle Load",
    "Vehicle Value",
    "Insurances",
  ]);
  const [activeStep, setActiveStep] = useState(0);
  const [driverType, setDriverType] = useState(null);
  const [engineSize, setEngineSize] = useState(null);
  const [ton, setTon] = useState(null);
  const [passenger, setPassenger] = useState(null);
  const [insuranceType, setInsuranceType] = useState(null);
  const [vehiclePrice, setVehiclePrice] = useState("");
  const [loadVehicleData, setLoadVehicleData] = useState({
    firstName: "",
    SecondName: "",
    ThirdName: "",
    FamilyName: "",
    mobileNumber: "",
    identityNumber: "",
    vehicleNumber: "",
    vehicleType: "",
    vehicleModel: "",
    productionYear: "",
  });

  const [basicFee, setBasicFee] = useState(0);
  const [comprehensiveFee, setComprehensiveFee] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    document.title = "Insurance | PhonePlay ";
    if (Array.isArray(insurances) && isEmpty(insurances)) {
      initInsurances();
    }
    refreshColumnStyle(setColumnStyle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (driverType) {
      let newSteps = [...steps];
      if (driverType.value === "private" && !engineSize) {
        newSteps[1] = "Engine Size";
      }
      if (driverType.value === "commercial" && !ton) {
        newSteps[1] = "Vehicle Load";
      }
      if (
        (driverType.value === "taxi_1_driver" ||
          driverType.value === "taxi_2_drivers" ||
          driverType.value === "taxi_multiple_drivers") &&
        !passenger
      ) {
        newSteps[1] = "Passengers count";
      }
      setSteps(newSteps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverType]);

  const handleVehicleDataChange = (e) => {
    const { name, value } = e.target;

    if (name === "vehicleType") {
      //To empty vehicle model when type is changed
      setLoadVehicleData({
        ...loadVehicleData,
        [name]: value,
        vehicleModel: "",
      });
      return;
    }

    setLoadVehicleData({
      ...loadVehicleData,
      [name]: value,
    });
  };

  const handleInsuranceCalculations = (e) => {
    e.preventDefault();
    getInsuranceCalculations(
      driverType?.value,
      engineSize?.value,
      ton?.value,
      passenger,
      loadVehicleData,
      setActiveStep
    );
  };

  useEffect(() => {
    console.log('Insurance calculation effect triggered:', {
      insuranceCalculations,
      vehiclePrice,
      insuranceType: insuranceType?.value,
      driverType: driverType?.value
    });

    if (!insuranceCalculations) {
      console.log('No insurance calculations data');
      return;
    }

    // Basic insurance only (no vehicle price needed)
    if (insuranceType?.value === "basic" || !vehiclePrice) {
      console.log('Setting basic insurance only');
      setBasicFee(insuranceCalculations.basic_free);
      setTotal(insuranceCalculations.basic_free);
      setComprehensiveFee(0);
      return;
    }

    // Comprehensive insurance with vehicle price
    if (insuranceType?.value === "comprehensive" && vehiclePrice) {
      console.log('Calculating comprehensive insurance');
      setBasicFee(insuranceCalculations.basic_free);

      // Special case for private vehicles <= 57000
      if (driverType?.value === "private" && Number(vehiclePrice) <= 57000) {
        console.log('Private vehicle <= 57000, using fixed 1000');
        setComprehensiveFee(1000);
        setTotal((1000 + Number(insuranceCalculations.basic_free)).toFixed(2));
        return;
      }

      // Calculate comprehensive fee based on percentage
      let comprehensiveValue = (
        (Number(vehiclePrice) / 100) *
        Number(insuranceCalculations.comprehensive_fee)
      ).toFixed(2);
      
      console.log('Comprehensive calculation:', {
        vehiclePrice,
        percentage: insuranceCalculations.comprehensive_fee,
        comprehensiveValue,
        basicFee: insuranceCalculations.basic_free
      });

      setComprehensiveFee(comprehensiveValue);
      setTotal(
        (
          Number(comprehensiveValue) + Number(insuranceCalculations.basic_free)
        ).toFixed(2)
      );
    }
  }, [insuranceCalculations, vehiclePrice, driverType, insuranceType]);

  const initInsurances = () => {
    getInsurances(Languages[intl.locale]);
  };

  const handleNext = (e) => {
    e.preventDefault();
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setLoadVehicleData({
      firstName: "",
      SecondName: "",
      ThirdName: "",
      FamilyName: "",
      mobileNumber: "",
      identityNumber: "",
      vehicleNumber: "",
      vehicleType: "",
      vehicleModel: "",
      productionYear: "",
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => Math.max(0, prevActiveStep - 1));
  };
  return (
    <Box 
      className={!isDarktheme ? "insurance-modern" : "dark-insurance-modern"}
      sx={{ 
        minHeight: '100vh',
        background: isDarktheme 
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' 
          : 'linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)',
        padding: '20px 16px'
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: '28px',
                mb: 0.5
              }}
            >
              {translate("Insurance")}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: isDarktheme ? '#a0aec0' : '#718096',
                fontSize: '14px'
              }}
            >
              {translate("Get your vehicle insurance easily")}
            </Typography>
          </Box>
          <SetHomePageButton 
            currentPath="/insurance"
            pageName={translate("Insurance")}
            variant="icon"
          />
        </Box>

        {/* Stepper Card */}
        <Card 
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            background: isDarktheme ? '#2d3748' : 'white',
            mb: 3,
            overflow: 'visible'
          }}
        >
          <CardContent sx={{ padding: '24px !important' }}>
            <Stepper
              steps={steps}
              setActiveStep={setActiveStep}
              activeStep={activeStep}
            />
          </CardContent>
        </Card>

        {/* Steps Content */}
        <Box className="row mb-5 wrapper">
          {activeStep === 0 && (
            <VehicleStepper
              driverType={driverType}
              driverTypes={driverTypes}
              setDriverType={setDriverType}
              handleNext={handleNext}
            />
          )}

          {activeStep === 1 && (
            <LoadStepper
              driverType={driverType}
              setEngineSize={setEngineSize}
              setPassenger={setPassenger}
              setTon={setTon}
              passenger={passenger}
              ton={ton}
              engineSize={engineSize}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          )}

          {activeStep === 2 && (
            <PriceStepper
              setVehiclePrice={setVehiclePrice}
              vehiclePrice={vehiclePrice}
              handleNext={handleInsuranceCalculations}
              handleBack={handleBack}
              insuranceType={insuranceType}
              setInsuranceType={setInsuranceType}
            />
          )}

          {activeStep === 3 && (
            <Box sx={{ width: '100%' }}>
              {/* Pricing Summary Card */}
              <Card 
                sx={{ 
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  background: isDarktheme ? '#2d3748' : 'white',
                  mb: 3,
                  maxWidth: '500px',
                  mx: 'auto'
                }}
              >
                <CardContent sx={{ padding: '24px !important' }}>
                  {basicFee && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 2,
                      pb: 2,
                      borderBottom: comprehensiveFee > 0 ? '1px solid #e2e8f0' : 'none'
                    }}>
                      <Typography sx={{ 
                        fontSize: '16px', 
                        fontWeight: 600,
                        color: isDarktheme ? '#f7fafc' : '#1a202c'
                      }}>
                        {translate("Basic Insurance Fee")}:
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '18px', 
                        fontWeight: 700,
                        color: '#667eea'
                      }}>
                        {basicFee} {translate("ILS")}
                      </Typography>
                    </Box>
                  )}
                  {comprehensiveFee > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 2,
                      pb: 2,
                      borderBottom: '1px solid #e2e8f0'
                    }}>
                      <Typography sx={{ 
                        fontSize: '16px', 
                        fontWeight: 600,
                        color: isDarktheme ? '#f7fafc' : '#1a202c'
                      }}>
                        {translate("Comprehensive Fee")}:
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '18px', 
                        fontWeight: 700,
                        color: '#667eea'
                      }}>
                        {comprehensiveFee} {translate("ILS")}
                      </Typography>
                    </Box>
                  )}
                  {total > 0 && comprehensiveFee > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center'
                    }}>
                      <Typography sx={{ 
                        fontSize: '18px', 
                        fontWeight: 700,
                        color: isDarktheme ? '#f7fafc' : '#1a202c'
                      }}>
                        {translate("total")}:
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '24px', 
                        fontWeight: 700,
                        color: '#10b981'
                      }}>
                        {total} {translate("ILS")}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Back Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <MuiButton
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    color: isDarktheme ? '#f7fafc' : '#667eea',
                    borderColor: isDarktheme ? '#4a5568' : '#667eea',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#764ba2',
                      backgroundColor: 'rgba(102, 126, 234, 0.05)',
                    },
                  }}
                >
                  {translate("Back")}
                </MuiButton>
              </Box>

              {/* Company Selection Title */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                  variant="h5"
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '22px',
                    color: isDarktheme ? '#f7fafc' : '#1a202c',
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: '3px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '2px'
                    }
                  }}
                >
                  {translate("Please Select A Company")}
                </Typography>
              </Box>

              {/* Insurance Companies Grid */}
              <Box className="row wrapper">
                {insurances.map((insurance, index) => (
                  <InsuranceCard
                    key={index.toString()}
                    columnStyle={columnStyle}
                    insurance={insurance}
                    handleVehicleDataChange={handleVehicleDataChange}
                    loadVehicleData={loadVehicleData}
                    submitInsuranceLoading={submitInsuranceLoading}
                    driverType={driverType}
                    engineSize={engineSize}
                    ton={ton}
                    passenger={passenger}
                    vehiclePrice={vehiclePrice}
                    insuranceType={insuranceType}
                    total={total}
                    handleReset={handleReset}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

const mapStateToProps = ({
  insurances: { list, insuranceCalculations, submitInsuranceLoading },
  auth: { user },
  darkTheme: { isDarktheme },
}) => ({
  user,
  insurances: list,
  insuranceCalculations,
  submitInsuranceLoading,
  isDarktheme,
});

export default connect(mapStateToProps, {
  getInsurances,
  getInsuranceCalculations,
})(Insurance);
